import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Avatar, type ComboboxItem } from '@node-space/ui-components'
import { useGetCustomersWalletProfileQuery } from 'hooks/queries/Customers/useCustomerWalletProfilesQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getCryptoCurrencies } from 'services/CurrencyService'

const SUPPORTED_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD']

const buildCurrencyOption = (code: string): ComboboxItem => ({
  value: code,
  leftText: code,
  LeftElement: <Avatar name={code.toLowerCase()} size="small" />,
})

const ALL_CURRENCIES_VALUE = ''

export const useCurrencyFilterOptions = (customerReference?: string) => {
  const { t } = useTranslation()
  const { enableWalletCreationV2 } = useFeatureFlags()

  const { data: cryptoCurrencies, isPending: isFetchingCrypto } = useQuery({
    queryKey: reactQueryKeys.cryptoCurrencies(),
    queryFn: getCryptoCurrencies,
    enabled: !enableWalletCreationV2,
    refetchOnReconnect: false,
  })

  const { data: walletProfiles, isFetching: isFetchingWalletProfiles } =
    useGetCustomersWalletProfileQuery(
      enableWalletCreationV2 && customerReference ? { customerId: customerReference } : undefined
    )

  const profileCurrencies = useMemo(() => {
    if (!walletProfiles?.profiles) return { allCurrencies: [] }

    const allCurrencies = new Set<string>()

    walletProfiles.profiles.forEach(profile => {
      profile.currencies?.forEach(currency => {
        allCurrencies.add(currency)
      })
    })

    return {
      allCurrencies: Array.from(allCurrencies).sort((a, b) => a.localeCompare(b)),
    }
  }, [walletProfiles])

  const currencyOptions: ComboboxItem[] = useMemo(() => {
    const allCurrenciesOption: ComboboxItem = {
      value: ALL_CURRENCIES_VALUE,
      leftText: t('customers.allCurrencies'),
    }

    if (enableWalletCreationV2 && walletProfiles?.profiles?.length) {
      const currencyOptions = profileCurrencies.allCurrencies.map(buildCurrencyOption)
      return [allCurrenciesOption, ...currencyOptions]
    }

    const allSupportedCurrencies = [
      ...SUPPORTED_FIAT_CURRENCIES,
      ...(cryptoCurrencies ?? []).map(currency => currency?.code),
    ]
    const uniqueCurrencies = Array.from(new Set(allSupportedCurrencies))
    const sortedOptions = uniqueCurrencies
      .sort((a, b) => a.localeCompare(b))
      .map(buildCurrencyOption)

    return [allCurrenciesOption, ...sortedOptions]
  }, [cryptoCurrencies, enableWalletCreationV2, profileCurrencies, walletProfiles, t])

  const isFetchingCurrencies = useMemo(() => {
    if (enableWalletCreationV2) {
      return isFetchingWalletProfiles
    }

    return isFetchingCrypto
  }, [isFetchingCrypto, enableWalletCreationV2, isFetchingWalletProfiles])

  return { currencyOptions, isFetchingCurrencies }
}
